import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  CircularProgress, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';

import { DATA_SOURCE_IDS, HISTORY_ENTRY_TYPES } from '../model/dataInputModel';
import {
  getImportCounterGroups,
  getImportResultPresentation,
} from '../model/importResultPresentation';
import mutationFeedback from '../model/mutationFeedback';
import { translateMessage } from '../../../shared/api';
import AppDialog from '../../../shared/components/AppDialog';
import { dataTableSx } from '../../../shared/components/TableHeader';
import { formatHistoryDateTime } from '../../../shared/utils/historyDateTime';
import {
  EMPTY_VALUE, formatArea, formatJpy, formatNumber,
} from '../../../shared/utils/number';

function RecalculationNotice({ recalculation }) {
  const { t } = useTranslation();
  const presentation = mutationFeedback({ recalculation });
  if (!recalculation || presentation.severity === 'success' || !presentation.messageKey) {
    return null;
  }
  return (
    <Alert severity={presentation.severity}>
      {t(presentation.messageKey, presentation.params)}
    </Alert>
  );
}

const present = (value) => value !== null && value !== undefined && value !== '';

export function historyEntryTitle(entry, t) {
  if (Array.isArray(entry?.fileNames) && entry.fileNames.length > 0) {
    return entry.fileNames.join(', ');
  }
  if (entry?.fileName) return entry.fileName;
  if (entry?.type === HISTORY_ENTRY_TYPES.SEAT_SYNC) {
    return t(entry.inputMethod === 'MANUAL_ENTRY'
      ? 'dataInput.history.event.seatManualInput'
      : 'dataInput.history.event.seatSync');
  }
  if (entry?.type === HISTORY_ENTRY_TYPES.CAT8_SYNC) {
    return t(entry.inputMethod === 'MANUAL_ENTRY'
      ? 'dataInput.history.event.cat8ManualInput'
      : 'dataInput.history.event.cat8Sync');
  }
  if (entry?.type === HISTORY_ENTRY_TYPES.CAT19_UPDATE) {
    return t(
      entry.changeType === 'CREATE'
        ? 'dataInput.history.event.cat19Create'
        : 'dataInput.history.event.cat19Update',
    );
  }
  if (entry?.noteKey) return t(entry.noteKey);
  if (entry?.note) return entry.note;
  return t('dataInput.history.event.operation');
}

const joinedMetrics = (items) => items.filter(Boolean).join(' · ');

export function HistorySummary({ entry }) {
  const { t } = useTranslation();
  if (entry.type === HISTORY_ENTRY_TYPES.SEAT_SYNC) {
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {joinedMetrics([
            entry.quarter ? `Q${entry.quarter}` : null,
            present(entry.metrics.totalSeats)
              ? t('dataInput.history.summary.seats', {
                count: formatNumber(entry.metrics.totalSeats, { max: 0 }),
              })
              : null,
          ]) || EMPTY_VALUE}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {joinedMetrics([
            present(entry.metrics.workingDays)
              ? t('dataInput.history.summary.workingDays', {
                count: formatNumber(entry.metrics.workingDays, { max: 0 }),
              })
              : null,
            present(entry.metrics.configVersion)
              ? t('dataInput.history.summary.configVersion', {
                version: entry.metrics.configVersion,
              })
              : null,
          ])}
        </Typography>
      </Stack>
    );
  }
  if (entry.type === HISTORY_ENTRY_TYPES.CAT8_SYNC) {
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {joinedMetrics([
            entry.quarter ? `Q${entry.quarter}` : null,
            present(entry.metrics.officeCount)
              ? t('dataInput.history.summary.offices', {
                count: formatNumber(entry.metrics.officeCount, { max: 0 }),
              })
              : null,
          ]) || EMPTY_VALUE}
        </Typography>
        {present(entry.metrics.totalAreaM2) && (
          <Typography variant="caption" color="text.secondary">
            {formatArea(entry.metrics.totalAreaM2, { withUnit: true })}
          </Typography>
        )}
      </Stack>
    );
  }
  if (entry.type === HISTORY_ENTRY_TYPES.CAT19_UPDATE) {
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {t(`dataInput.history.changeType.${entry.changeType || 'UPDATE'}`)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatJpy(entry.metrics.amountJpy)}
        </Typography>
      </Stack>
    );
  }
  if (entry.sourceId === DATA_SOURCE_IDS.SCOPE_3_EWASTE) {
    const counterGroups = getImportCounterGroups(entry.sourceId, entry.stats);
    const counters = counterGroups.flatMap(group => group.values);
    const sourceValues = counters.find(item => item.key === 'source_values_checked');
    const annualValue = counters.find(item => item.key === 'annual_weee_value');
    const issues = counterGroups
      .find(group => group.key === 'issues')
      ?.values.reduce((total, item) => total + Number(item.value || 0), 0) || 0;
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {sourceValues
            ? `${t(sourceValues.labelKey)}: ${formatNumber(sourceValues.value, { max: 0 })}`
            : EMPTY_VALUE}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {joinedMetrics([
            annualValue
              ? `${t(annualValue.labelKey)}: ${t(annualValue.valueKey)}`
              : null,
            issues > 0
              ? t('dataInput.history.summary.issues', {
                count: formatNumber(issues, { max: 0 }),
              })
              : null,
          ])}
        </Typography>
      </Stack>
    );
  }

  if (entry.sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT) {
    const included = Number(entry.stats.included_rows);
    const total = Number(entry.stats.total_rows);
    const issues = Number(entry.stats.skipped_rows || 0)
      + Number(entry.stats.ambiguous_rows || 0);
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {Number.isFinite(included) && Number.isFinite(total)
            ? t('dataInput.history.summary.included', {
              processed: formatNumber(included, { max: 0 }),
              total: formatNumber(total, { max: 0 }),
            })
            : EMPTY_VALUE}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {joinedMetrics([
            issues > 0
              ? t('dataInput.history.summary.issues', {
                count: formatNumber(issues, { max: 0 }),
              })
              : null,
          ])}
        </Typography>
      </Stack>
    );
  }

  const persistedRows = Number(entry.stats.inserted_rows || 0)
    + Number(entry.stats.updated_rows || 0)
    + Number(entry.stats.unchanged_rows || 0);
  const processed = entry.stats.processed_rows
    ?? (persistedRows > 0 ? persistedRows : entry.stats.total_rows);
  const total = entry.stats.total_rows;
  const changed = Number(entry.stats.inserted_rows || 0) + Number(entry.stats.updated_rows || 0);
  const issues = Number(entry.stats.skipped_rows || 0)
    + Number(entry.stats.invalid_rows || 0)
    + Number(entry.stats.ambiguous_rows || 0);
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {present(processed) || present(total)
          ? t('dataInput.history.summary.processed', {
            processed: formatNumber(processed ?? 0, { max: 0 }),
            total: formatNumber(total ?? 0, { max: 0 }),
          })
          : EMPTY_VALUE}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {joinedMetrics([
          changed > 0
            ? t('dataInput.history.summary.changed', {
              count: formatNumber(changed, { max: 0 }),
            })
            : null,
          issues > 0
            ? t('dataInput.history.summary.issues', {
              count: formatNumber(issues, { max: 0 }),
            })
            : null,
        ])}
      </Typography>
    </Stack>
  );
}

function Metadata({ entry }) {
  const { t, i18n } = useTranslation();
  const actor = entry.createdByKey ? t(entry.createdByKey) : entry.createdBy;
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.history.column.date')}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 650, textAlign: 'right' }}>
          {formatHistoryDateTime(entry.createdAt, i18n.language)}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.history.column.actor')}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 650, textAlign: 'right' }}>
          {actor || EMPTY_VALUE}
        </Typography>
      </Stack>
    </Stack>
  );
}

function ImportDetail({ entry }) {
  const { t } = useTranslation();
  const presentation = getImportResultPresentation(entry);
  const groups = getImportCounterGroups(entry.sourceId, entry.stats);
  const message = presentation?.messageKey
    ? t(presentation.messageKey, presentation.params)
    : translateMessage(
      presentation?.messageCode || 'IMPORT_PROCESSING_FAILED',
      500,
      presentation?.params || entry.params,
    );

  return (
    <Stack spacing={2}>
      {presentation && (
        <Alert severity={presentation.severity}>{message}</Alert>
      )}
      {presentation?.causeCode && (
        <Typography variant="body2" color="text.secondary">
          {translateMessage(presentation.causeCode, 500, presentation.params)}
        </Typography>
      )}
      {(entry.status === 'FAILED' || entry.status === 'PARTIAL')
        && (entry.params?.failed_stage || entry.params?.error_detail) && (
        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon fontSize="small" />}>
            <Typography variant="caption" color="text.secondary">
              {t('dataInput.history.technical.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {t('dataInput.history.technical.code')}: {entry.message}
              </Typography>
              {entry.params?.failed_stage && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {t('dataInput.history.technical.stage')}: {entry.params.failed_stage}
                </Typography>
              )}
              {entry.params?.error_detail && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {t('dataInput.history.technical.errorDetail')}: {entry.params.error_detail}
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      {groups.map((group) => (
        <Box key={group.key}>
          <Typography variant="overline" color="text.secondary">
            {t(`dataInput.history.counterGroup.${group.key}`)}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              mt: 0.5,
            }}
          >
            {group.values.map(({
              key, labelKey, valueKey, value,
            }) => (
              <Paper key={key} variant="outlined" sx={{ p: 1.1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(labelKey)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>
                  {valueKey ? t(valueKey) : formatNumber(value, { max: 0 })}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function Cat19Snapshot({ label, value }) {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      {value ? (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('dataInput.history.amount')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 750 }}>
              {formatJpy(value.amount_jpy)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('dataInput.history.noteLabel')}
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
              {value.note || EMPTY_VALUE}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('dataInput.history.notAvailable')}
        </Typography>
      )}
    </Paper>
  );
}

function Cat19Detail({ entry }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.25}>
      <Cat19Snapshot label={t('dataInput.history.before')} value={entry.detail?.before} />
      <Cat19Snapshot label={t('dataInput.history.after')} value={entry.detail?.after} />
    </Stack>
  );
}

function DriverDetail({ entry, detail }) {
  const { t } = useTranslation();
  const seat = entry.type === HISTORY_ENTRY_TYPES.SEAT_SYNC;
  const items = detail?.items || [];
  return (
    <Stack spacing={1.5}>
      <RecalculationNotice recalculation={entry.recalculation} />
      <Paper variant="outlined" sx={{ p: 1.25 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {t('dataInput.history.period')}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {detail?.period_label || (entry.quarter ? `Q${entry.quarter}` : EMPTY_VALUE)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            {t(
              seat
                ? 'dataInput.history.requestedAsOf'
                : 'dataInput.history.sourceAsOf',
            )}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {(
              seat
                ? detail?.requested_as_of_date || entry.metrics.requestedAsOfDate
                : detail?.source_as_of_date || entry.metrics.sourceAsOfDate
            ) || EMPTY_VALUE}
          </Typography>
        </Stack>
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader sx={dataTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>{t('dataInput.history.location')}</TableCell>
              <TableCell align="right">
                {t(seat ? 'dataInput.history.totalSeats' : 'dataInput.history.area')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const itemKey = item.office_ref_id
                || item.electricity_location_code
                || item.location_code
                || index;
              const label = item.electricity_location_code
                || item.location_code
                || item.report_location_name
                || EMPTY_VALUE;
              return (
                <TableRow key={itemKey}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                      {label}
                    </Typography>
                    {item.report_location_name && item.report_location_name !== label && (
                      <Typography variant="caption" color="text.secondary">
                        {item.report_location_name}
                      </Typography>
                    )}
                    {(item.floors || []).map((floor, floorIndex) => (
                      <Typography
                        key={`${floor.floor_name || floorIndex}-${floorIndex}`}
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25, pl: 1 }}
                      >
                        {floor.floor_name}: {formatNumber(floor.total_seats, { max: 0 })}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    {seat
                      ? formatNumber(item.total_seats, { max: 0 })
                      : formatArea(item.area_m2, { withUnit: true })}
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    {t('dataInput.history.noSnapshotItems')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export default function HistoryDetailDialog({
  open, entry, detail, loading, error, onClose, onExited, onRetry,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const remote = entry?.type === HISTORY_ENTRY_TYPES.SEAT_SYNC
    || entry?.type === HISTORY_ENTRY_TYPES.CAT8_SYNC;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      TransitionProps={{ onExited }}
      aria-labelledby="history-detail-dialog-title"
      sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
      PaperProps={{ sx: { maxHeight: { xs: '100%', sm: 'calc(100vh - 64px)' } } }}
    >
      <DialogTitle id="history-detail-dialog-title" sx={{ pr: 6 }}>
        {t('dataInput.history.details')}
        {entry && (
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 750, overflowWrap: 'anywhere' }}>
            {historyEntryTitle(entry, t)}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {entry && (
          <>
            <Metadata entry={entry} />
            <Divider sx={{ my: 2 }} />
            {loading && (
              <Box sx={{ display: 'grid', minHeight: 180, placeItems: 'center' }}>
                <CircularProgress size={30} aria-label={t('dataInput.history.loadingDetails')} />
              </Box>
            )}
            {!loading && error && (
              <Alert
                severity="error"
                action={(
                  <Button color="inherit" size="small" startIcon={<ReplayRoundedIcon />} onClick={onRetry}>
                    {t('common.retry')}
                  </Button>
                )}
              >
                {error}
              </Alert>
            )}
            {!loading && !error && entry.type === HISTORY_ENTRY_TYPES.IMPORT && (
              <ImportDetail entry={entry} />
            )}
            {!loading && !error && entry.type === HISTORY_ENTRY_TYPES.CAT19_UPDATE && (
              <Cat19Detail entry={entry} />
            )}
            {!loading && !error && remote && <DriverDetail entry={entry} detail={detail} />}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </AppDialog>
  );
}
